// Who pays when a policy is EDITED by someone other than its creator. Pure rules, no screen code.
//
// The creator is the policy's stored userId / clientId (an edit never overwrites them); the editor is whoever is
// logged in. When they differ the editor chooses `chargeTo`: "creator" (default) or "editor". The server refunds
// whoever really paid and charges the chosen payer (see RayalBrokers-backend README, "Editing a policy").

const idOf = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
};

/** The policy's creator ({ userId, clientId }) as stored. */
export const creatorOf = (policy) => ({ userId: idOf(policy?.userId), clientId: idOf(policy?.clientId) });

/** True when somebody other than the creator is editing (the creator is unknown for very old policies: then false). */
export const isEditedByAnotherPerson = (policy, editorUserId) => {
  const creator = creatorOf(policy).userId;
  return Boolean(creator && editorUserId && creator !== String(editorUserId));
};

/** "creator" or "editor"; anything else means the creator pays. */
export const normalizeChargeTo = (value) => (value === "editor" ? "editor" : "creator");

/** The client whose Company Wallet pays the new charge. */
export const payerClientId = ({ chargeTo, policy, editorClientId }) =>
  normalizeChargeTo(chargeTo) === "editor"
    ? String(editorClientId || "")
    : creatorOf(policy).clientId || String(editorClientId || "");

/**
 * What this policy already has paid, and to whom the edit gives it back, only counts towards the payer's balance
 * check when the payer's wallet IS the wallet that paid (same client); otherwise the refund goes elsewhere.
 */
export const refundCountsForPayer = ({ chargeTo, policy, editorClientId }) => {
  const original = creatorOf(policy).clientId || String(editorClientId || "");
  return payerClientId({ chargeTo, policy, editorClientId }) === original;
};

const rupees = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The money line of one edit in the Job History, from the server's editHistory entry. null for an edit made before
 * this was recorded (no amounts), so nothing is invented for it.
 */
export const editMoneyText = (edit) => {
  if (!edit || (edit.charged === undefined && edit.refunded === undefined)) return null;
  const payer = edit.chargedToName || edit.chargedTo || "the payer";
  const refunded = Number(edit.refunded) || 0;
  const charged = Number(edit.charged) || 0;
  if (edit.rerunCompleted) {
    return `Bought again: ${rupees(charged)} charged to ${payer}${refunded > 0 ? `; ${rupees(refunded)} of unfinished parts given back to ${edit.refundedToName || "the original payer"}` : ""}`;
  }
  if (refunded === 0 && charged === 0) return `No money moved (${payer})`;
  const back =
    refunded > 0
      ? `${rupees(refunded)} given back to ${edit.refundedToName || "the original payer"}`
      : "nothing given back";
  return `${back}; ${rupees(charged)} charged to ${payer}`;
};
